import { modelsApi } from '@/api/models.api';
import { CARD_PADDED } from '@/components/common/surfaces';
import { CubeFocus } from '@phosphor-icons/react';
import { useEffect, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProjectUploadingOverlayProps {
  taskId: string | null;
  /** Progress 0–100 when upload queue context drives the task */
  progressFromContext: number;
  /** Open SSE when task id is only known from storage (e.g. refresh) — avoids duplicate stream while context already tracks */
  subscribeToTaskStream: boolean;
  onComplete: () => void;
  onStreamError?: () => void;
}

export const ProjectUploadingOverlay: FC<ProjectUploadingOverlayProps> = ({
  taskId,
  progressFromContext,
  subscribeToTaskStream,
  onComplete,
  onStreamError,
}) => {
  const { t } = useTranslation();
  const [localProgress, setLocalProgress] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const onStreamErrorRef = useRef(onStreamError);
  onCompleteRef.current = onComplete;
  onStreamErrorRef.current = onStreamError;

  useEffect(() => {
    if (!subscribeToTaskStream || !taskId) {
      return;
    }

    const controller = new AbortController();
    void (async () => {
      try {
        await modelsApi.trackProgressSSE(
          taskId,
          (p) => {
            setLocalProgress(p.progress);
          },
          controller.signal
        );
        onCompleteRef.current();
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
          return;
        }
        console.error('Project upload overlay SSE error:', e);
        onStreamErrorRef.current?.();
        onCompleteRef.current();
      }
    })();

    return () => controller.abort();
  }, [subscribeToTaskStream, taskId]);

  const progress = subscribeToTaskStream ? localProgress : progressFromContext;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 px-4 backdrop-blur-sm"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <div className={`${CARD_PADDED} flex w-full max-w-md flex-col items-center gap-4 shadow-lg`}>
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
          <CubeFocus size={28} aria-hidden="true" />
        </span>
        <p className="m-0 text-center text-lg font-semibold text-gray-900">
          {t('projectDetail.processingOverlayTitle')}
        </p>
        <p className="m-0 text-center text-sm text-gray-500 [text-wrap:pretty]">
          {t('projectDetail.processingOverlayDescription')}
        </p>
        <div className="flex w-full flex-col gap-2">
          <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 motion-reduce:transition-none"
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
          <p className="m-0 text-center text-xs tabular-nums text-gray-500">{Math.round(progress)}%</p>
        </div>
      </div>
    </div>
  );
};
